import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, CircularProgress, Alert, Chip, IconButton,
  Collapse, Divider, Button, TextField, InputAdornment, Dialog,
  DialogTitle, DialogContent, Stack,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Description as HardcopyIcon,
  RecordVoiceOver as CounselIcon,
  NoteAlt as NoteIcon,
  Science as VerifyIcon,
  Add as AddIcon,
  Send as SendIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../services/api';
import notesService from '../../services/notesService';
import HardcopyViewer from '../common/HardcopyViewer';
import CounselingDialog from '../counseling/CounselingDialog';
import { useBatchUserLookup } from '../../hooks/useUserLookup';
import { getCounselingStatusDisplay, deriveCounselFlagsFromCounseling, pickCounselingRecordForRx } from '../../constants/counselingConstants';
import RxCounselingCard from './RxCounselingCard';
import { ISEC_SUMMARY_KEY, ISEC_SUMMARY_LABEL } from '../../constants/isecQuestions';
import { openReportWindow, renderRxReport, fetchReportHardcopyImages } from './rxReportPrint';
import { headerStripSurface } from '../../theme/gridSurfaces';
import { useVerificationPhotos } from '../../hooks/useVerificationPhotos';
import { VerificationPhotoLightbox } from '../product-verification/VerificationPhotoLightbox';
import type { VerificationPhoto } from '../../services/verificationPhotoService';
import {
  fetchRxTimelineEntries,
  type CounselingRecord,
  type PatientNote,
  type RxTimelineEntry,
} from './rxTimelineData';

const IESC_LABELS: Record<string, string> = {
  indication: 'Indication',
  effectiveness: 'Effectiveness',
  safety: 'Safety',
  compliance: 'Compliance',
};

// Labels for rendering clinicalNotes entries — includes the free-text summary
// key, which is a note but not an IESC letter (so it must not get a chip).
const IESC_NOTE_LABELS: Record<string, string> = {
  ...IESC_LABELS,
  [ISEC_SUMMARY_KEY]: ISEC_SUMMARY_LABEL,
};

interface RxTimelineTabProps {
  patientId: string;
  pharmacyId?: string;
  patientName?: string;
}

const RxTimelineTab: React.FC<RxTimelineTabProps> = ({ patientId, pharmacyId, patientName }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<RxTimelineEntry[]>([]);
  const [expandedRx, setExpandedRx] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [hardcopyRx, setHardcopyRx] = useState<{ rxNumber: string; orderId: string } | null>(null);
  const [hardcopySignatures, setHardcopySignatures] = useState<any[]>([]);
  const [openPhoto, setOpenPhoto] = useState<VerificationPhoto | null>(null);
  const [counselingDialogRecord, setCounselingDialogRecord] = useState<{ entry: RxTimelineEntry; record: CounselingRecord } | null>(null);
  const [addingNoteRx, setAddingNoteRx] = useState<string | null>(null);
  const [newNoteText, setNewNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Collect all auth0 IDs from counseling records for batch user lookup
  const counselingUserIds = useMemo(() => {
    const ids: (string | undefined | null)[] = [];
    entries.forEach(e => e.counselingRecords.forEach(rec => {
      ids.push(rec.completedBy, rec.createdBy);
    }));
    return ids;
  }, [entries]);
  const { userNames: counselingUserMap } = useBatchUserLookup(counselingUserIds);

  const displayUser = useCallback((value: string | undefined | null): string => {
    if (!value) return '';
    return counselingUserMap.get(value) || value;
  }, [counselingUserMap]);

  // Fetch signatures when hardcopy dialog opens
  useEffect(() => {
    if (!hardcopyRx || !pharmacyId) {
      setHardcopySignatures([]);
      return;
    }
    api.get(`/api/rx/${pharmacyId}/signatures/${hardcopyRx.rxNumber}`)
      .then((res) => {
        if (res.data?.success && res.data?.data?.signatures) {
          setHardcopySignatures(res.data.data.signatures);
        } else if (res.data?.success && Array.isArray(res.data?.data)) {
          setHardcopySignatures(res.data.data);
        } else {
          setHardcopySignatures([]);
        }
      })
      .catch(() => setHardcopySignatures([]));
  }, [hardcopyRx, pharmacyId]);

  // PRX-68: the hardcopy dialog's Rx belongs to one order — fetch that order's
  // product-verification photos once (shared cache key with the order details
  // page and the timeline) rather than a photo query per Rx.
  const { data: hardcopyPhotosData } = useVerificationPhotos(pharmacyId, hardcopyRx?.orderId);
  const hardcopyPhotos = Array.isArray(hardcopyPhotosData) ? hardcopyPhotosData : [];

  useEffect(() => {
    fetchAllData();
  }, [patientId]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Assembly (verification + counseling + notes merge) lives in the shared
      // rxTimelineData module so the Counseling History print reuses the exact
      // same per-Rx object (GP-1177).
      setEntries(await fetchRxTimelineEntries(patientId));
    } catch (err) {
      console.error('Error fetching RX timeline:', err);
      enqueueSnackbar('Failed to load RX history', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = useCallback(async (rxNumber: string, orderId: string) => {
    if (!newNoteText.trim() || savingNote) return;
    setSavingNote(true);
    try {
      await notesService.createNoteForPanel({
        entityType: 'patient',
        entityId: patientId,
        content: newNoteText.trim(),
        author: {
          id: user?.sub || 'unknown',
          name: user?.name || user?.email || 'Current User',
        },
        type: 'clinical',
        tags: [`rx:${rxNumber}`],
        relatedPrescriptionId: rxNumber,
        relatedOrderId: orderId || undefined,
      });
      setNewNoteText('');
      setAddingNoteRx(null);
      enqueueSnackbar('Note added', { variant: 'success' });
      await fetchAllData();
    } catch (err) {
      console.error('Error adding note:', err);
      enqueueSnackbar('Failed to add note', { variant: 'error' });
    } finally {
      setSavingNote(false);
    }
  }, [newNoteText, savingNote, patientId, user, enqueueSnackbar]);

  // GP-1177: compose this one Rx's verification, counseling checklist, notes, AND
  // hardcopy/script images into a single printable/shareable report (Save as PDF
  // from the print dialog), instead of hunting across the RX History, Counseling
  // History, All Notes, and Hardcopy surfaces. The window is opened synchronously
  // (preserving the click gesture so the popup isn't blocked) before the async
  // hardcopy fetch.
  const handlePrintReport = useCallback(async (entry: RxTimelineEntry) => {
    const win = openReportWindow();
    if (!win) {
      enqueueSnackbar('Please allow pop-ups to generate the report.', { variant: 'warning' });
      return;
    }
    // Hardcopy images are best-effort — fetchReportHardcopyImages swallows its
    // own errors, so the report still renders (without images) on failure.
    const { images, unavailable } = await fetchReportHardcopyImages(pharmacyId, patientId, entry.rxNumber);
    renderRxReport(win, entry, {
      patientName,
      resolveUser: displayUser,
      hardcopyImages: images,
      hardcopyUnavailable: unavailable,
    });
  }, [patientName, displayUser, enqueueSnackbar, pharmacyId, patientId]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return entries;
    const q = searchQuery.toLowerCase();
    return entries.filter(e =>
      e.rxNumber.toLowerCase().includes(q) ||
      e.medicationName.toLowerCase().includes(q) ||
      e.verifiedBy?.toLowerCase().includes(q)
    );
  }, [entries, searchQuery]);

  const getCounselingStatusChip = (records: CounselingRecord[]) => {
    if (records.length === 0) return null;
    const latest = records[0];
    const s = getCounselingStatusDisplay(latest.status, latest.declinedReason);
    return (
      <Chip
        label={s.label}
        size="small"
        color={s.color}
        sx={{ height: 20, fontSize: '0.7rem' }}
      />
    );
  };

  const getIescChips = (dv: any) => {
    if (!dv?.clinicalChecks) return null;
    const checks = dv.clinicalChecks;
    return (
      <Box sx={{ display: 'flex', gap: 0.25 }}>
        {Object.entries(IESC_LABELS).map(([key, label]) => (
          <Chip
            key={key}
            label={label.charAt(0)}
            size="small"
            color={checks[key] ? 'success' : 'default'}
            sx={{ width: 22, height: 18, fontSize: '0.6rem', fontWeight: 700, '& .MuiChip-label': { px: 0 } }}
          />
        ))}
      </Box>
    );
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  }

  if (entries.length === 0) {
    return <Alert severity="info" sx={{ m: 2 }}>No clinical verification history found for this patient.</Alert>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <TextField
        size="small"
        placeholder="Search by RX#, drug name, or pharmacist..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18 }} /></InputAdornment> }}
        sx={{ mb: 2, width: 360 }}
      />

      <Stack spacing={1}>
        {filtered.map((entry) => {
          const isExpanded = expandedRx === entry.rxNumber;
          const dv = entry.drugVerification;
          return (
            <Paper key={entry.rxNumber} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
              {/* Summary row */}
              <Box
                sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => setExpandedRx(isExpanded ? null : entry.rxNumber)}
              >
                <IconButton size="small" sx={{ p: 0 }}>
                  {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace' }}>RX #{entry.rxNumber}</Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>— {entry.medicationName}</Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {entry.verifiedAt ? format(new Date(entry.verifiedAt), 'MMM d, yyyy') : '—'}
                    {entry.verifiedBy && ` · ${displayUser(entry.verifiedBy)}`}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {getIescChips(dv)}
                  {entry.notes.length > 0 && (
                    <Chip icon={<NoteIcon sx={{ fontSize: 12 }} />} label={entry.notes.length} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                  )}
                  {getCounselingStatusChip(entry.counselingRecords)}
                  {/* GP-1177: one-click consolidated report for this Rx (verification +
                      counseling checklist + notes), printable / Save-as-PDF to share. */}
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                    onClick={(e) => { e.stopPropagation(); handlePrintReport(entry); }}
                    sx={{ height: 24, fontSize: '0.7rem', textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    Print Report
                  </Button>
                </Box>
              </Box>

              {/* Expanded content */}
              <Collapse in={isExpanded}>
                <Box sx={{ px: 2, pb: 2, pt: 0.5, bgcolor: headerStripSurface }}>
                  {/* Verification */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                      <VerifyIcon sx={{ fontSize: 14 }} /> VERIFICATION
                    </Typography>
                    {dv ? (
                      <Box>
                        <Box sx={{ display: 'flex', gap: 3, mb: 1, flexWrap: 'wrap' }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">IESC</Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.25 }}>
                              {Object.entries(IESC_LABELS).map(([key, label]) => (
                                <Chip key={key} label={`${label.charAt(0)}${dv.clinicalChecks?.[key] ? '✓' : ''}`} size="small"
                                  color={dv.clinicalChecks?.[key] ? 'success' : 'default'} variant="outlined"
                                  sx={{ height: 22, fontSize: '0.7rem', fontWeight: 600 }} />
                              ))}
                            </Box>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Days Supply</Typography>
                            <Typography variant="body2" fontWeight={600}>{dv.verifiedDaysSupply || entry.verifiedDaysSupply || entry.daysSupply || '—'}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Technical</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {dv.technicalChecks && Object.values(dv.technicalChecks).every(Boolean) ? '✓ Verified' : '—'}
                            </Typography>
                          </Box>
                        </Box>
                        {dv.clinicalNotes && Object.entries(dv.clinicalNotes).some(([, v]) => v) && (
                          <Box sx={{ mb: 1, pl: 1, borderLeft: 2, borderColor: 'primary.light' }}>
                            {Object.entries(dv.clinicalNotes).map(([key, note]) => note ? (
                              <Typography key={key} variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
                                <strong>{IESC_NOTE_LABELS[key] || key}:</strong> {note as string}
                              </Typography>
                            ) : null)}
                          </Box>
                        )}
                        {dv.counselFlags && Object.entries(dv.counselFlags).some(([, v]) => v) && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {Object.entries(dv.counselFlags).filter(([, v]) => v).map(([key]) => (
                              <Chip key={key} label={key.replace(/([A-Z])/g, ' $1').trim()} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                            ))}
                          </Box>
                        )}
                        <Button size="small" variant="outlined" startIcon={<HardcopyIcon sx={{ fontSize: 14 }} />}
                          onClick={(e) => { e.stopPropagation(); setHardcopyRx({ rxNumber: entry.rxNumber, orderId: entry.orderId }); }}>
                          View Hardcopy
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Verified by {displayUser(entry.verifiedBy)} on {entry.verifiedAt ? format(new Date(entry.verifiedAt), 'MMM d, yyyy HH:mm') : '—'}
                        </Typography>
                        <Button size="small" variant="outlined" startIcon={<HardcopyIcon sx={{ fontSize: 14 }} />}
                          onClick={(e) => { e.stopPropagation(); setHardcopyRx({ rxNumber: entry.rxNumber, orderId: entry.orderId }); }}>
                          View Hardcopy
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Counseling */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                      <CounselIcon sx={{ fontSize: 14 }} /> COUNSELING
                    </Typography>
                    {/* Same card the Rx Detail dialog renders (GP-1358). */}
                    <RxCounselingCard
                      records={entry.counselingRecords}
                      resolveUser={displayUser}
                      showHeader={false}
                      onOpenRecord={(rec) => setCounselingDialogRecord({ entry, record: rec })}
                    />
                  </Box>

                  <Divider sx={{ my: 1 }} />

                  {/* Notes */}
                  <Box>
                    <Typography variant="caption" fontWeight={700} color="primary.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                      <NoteIcon sx={{ fontSize: 14 }} /> NOTES ({entry.notes.length})
                    </Typography>
                    {entry.notes.length > 0 ? (
                      <Stack spacing={0.75}>
                        {entry.notes.map((note) => (
                          <Box key={note.id} sx={{ pl: 1, borderLeft: 2, borderColor: note.type === 'clinical_verification' ? 'success.main' : 'divider' }}>
                            {note.type === 'clinical_verification' && (
                              <Chip label="Clinical Verification" size="small" color="success" variant="outlined"
                                sx={{ height: 18, fontSize: '0.6rem', mb: 0.25 }} />
                            )}
                            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>{note.content}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {note.author?.name} · {format(new Date(note.createdAt), 'MMM d, yyyy HH:mm')}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">No notes</Typography>
                    )}
                    {addingNoteRx === entry.rxNumber ? (
                      <Box sx={{ mt: 1, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
                        <TextField
                          size="small"
                          multiline
                          minRows={1}
                          maxRows={3}
                          placeholder="Add a clinical note..."
                          value={newNoteText}
                          onChange={(e) => setNewNoteText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              handleAddNote(entry.rxNumber, entry.orderId);
                            }
                            if (e.key === 'Escape') {
                              setAddingNoteRx(null);
                              setNewNoteText('');
                            }
                          }}
                          autoFocus
                          fullWidth
                          sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem' } }}
                        />
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleAddNote(entry.rxNumber, entry.orderId)}
                          disabled={!newNoteText.trim() || savingNote}
                        >
                          <SendIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => { e.stopPropagation(); setAddingNoteRx(entry.rxNumber); setNewNoteText(''); }}
                        sx={{ mt: 0.75, fontSize: '0.7rem', textTransform: 'none' }}
                      >
                        Add Note
                      </Button>
                    )}
                  </Box>
                </Box>
              </Collapse>
            </Paper>
          );
        })}
      </Stack>

      {/* Hardcopy Dialog */}
      <Dialog
        open={!!hardcopyRx}
        onClose={() => { setHardcopyRx(null); setOpenPhoto(null); }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Hardcopy — RX #{hardcopyRx?.rxNumber}
          <Button onClick={() => { setHardcopyRx(null); setOpenPhoto(null); }} color="inherit">Close</Button>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {hardcopyRx && (() => {
            const entry = entries.find(e => e.rxNumber === hardcopyRx.rxNumber);
            const dv = entry?.drugVerification;
            // Carry the counseling record's method (e.g. "by phone") and decline
            // status onto the hardcopy counsel checkboxes — GP-1110. The latest
            // counseling record wins; explicit verification flags are the base.
            const counselFlags = deriveCounselFlagsFromCounseling(
              pickCounselingRecordForRx(entry?.counselingRecords, hardcopyRx.rxNumber),
              dv?.counselFlags,
            );
            return (
              <Box sx={{ p: 2 }}>
                <HardcopyViewer
                  rxNumber={hardcopyRx.rxNumber}
                  pharmacyId={pharmacyId}
                  compact={false}
                  editable={false}
                  signatures={hardcopySignatures}
                  counselFlags={counselFlags}
                  patientId={patientId}
                  verificationPhotos={hardcopyPhotos}
                  onOpenVerificationPhoto={(photo) => setOpenPhoto(photo)}
                />
              </Box>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Product verification photo lightbox (PRX-68) */}
      <VerificationPhotoLightbox
        open={openPhoto !== null}
        onClose={() => setOpenPhoto(null)}
        pharmacyId={pharmacyId || ''}
        orderId={hardcopyRx?.orderId || ''}
        photo={openPhoto}
        orderNumber={entries.find(e => e.rxNumber === hardcopyRx?.rxNumber)?.orderNumber}
      />

      {/* Counseling Dialog */}
      {counselingDialogRecord && (
        <CounselingDialog
          open={true}
          onClose={() => { setCounselingDialogRecord(null); fetchAllData(); }}
          orderData={{
            pharmacyId: pharmacyId || '',
            orderId: counselingDialogRecord.record.orderId || counselingDialogRecord.entry.orderId || undefined,
            orderNumber: counselingDialogRecord.entry.orderNumber,
            rxNumber: counselingDialogRecord.entry.rxNumber,
            patientId: patientId,
            patientName: counselingDialogRecord.record.patientName,
            drugBrand: counselingDialogRecord.record.drugName || counselingDialogRecord.entry.medicationName,
            din: counselingDialogRecord.record.din,
            doctorName: counselingDialogRecord.record.doctorName,
          }}
          existingRecordId={counselingDialogRecord.record.id}
          existingChecklist={counselingDialogRecord.record.checklist as any}
          existingStatus={counselingDialogRecord.record.status}
          existingCounselingBy={counselingDialogRecord.record.counselingBy}
          existingMethod={counselingDialogRecord.record.method}
          existingComments={counselingDialogRecord.record.comments}
          existingDeclinedReason={counselingDialogRecord.record.declinedReason}
          existingAgentName={counselingDialogRecord.record.agentName}
          existingAgentRelationship={counselingDialogRecord.record.agentRelationship}
          existingDateCounselled={counselingDialogRecord.record.dateCounselled}
          onComplete={() => {
            setCounselingDialogRecord(null);
            fetchAllData();
          }}
        />
      )}
    </Box>
  );
};

export default RxTimelineTab;
